/**
 * The source-type registry: every external system a record can carry a
 * `record_sources` row from, and what an interface needs to show it — label,
 * icon, brand color, a link back to the external system — plus whether the
 * unlinked-source search covers it.
 *
 * A code constant, not a table: the sync jobs that write these rows
 * (backend/db/sql/pipedrive_*.sql, backend/harmonic, the Airtable/Luma/HubSpot
 * imports, the mail and calendar savers) are code too, so a new source type is a
 * code change either way. `record_sources.source_type` itself is a free string;
 * a type missing here still reads (the helpers fall back), it just renders
 * generically and the MCP's `add_sources` refuses it.
 *
 * Pure data and functions — the web app and the MCP both read it.
 */

import { capitalize } from "../utils/stringFormatter.js"

/** The record types that carry sources. */
export type SourceRecordType = "person" | "organization" | "event" | "deal" | "document"

/**
 * A FontAwesome icon: a solid icon's name ("table"), or a [prefix, name] pair
 * for another style (["fab", "hubspot"] for a brand). Data only — the interface
 * owns the icon library.
 */
export type SourceIcon = string | [string, string]

/** One source type. */
export interface SourceTypeConfig {
  /** The full type, as stored in `record_sources.source_type`: "pipedrive_person". */
  type: string
  /** The integration's short key, shared across record types: "pipedrive". */
  key: string
  /** The record type this source belongs to. */
  recordType: SourceRecordType
  /** Human-readable name: "Pipedrive". */
  label: string
  icon: SourceIcon
  /** Brand color. */
  color?: string
  /** A link to the source in its own system, `{id}` standing for the source id. */
  externalUrlTemplate?: string
  /** Whether the unlinked-source search (find a source no record claims yet) covers it. */
  isSearchable: boolean
}

const PIPEDRIVE = { label: "Pipedrive", icon: ["fab", "product-hunt"], color: "#4a689b" } as const
const AIRTABLE = { label: "Airtable", icon: "table", color: "#245b71" } as const
const HUBSPOT = { label: "HubSpot", icon: ["fab", "hubspot"], color: "#ff7a59" } as const
const LUMA = { label: "Luma", icon: "calendar-check", color: "#9b51e0" } as const
const SLACK = { label: "Slack", icon: ["fab", "slack"], color: "#611f69" } as const
const SANITY = { label: "Sanity", icon: "s", color: "#f03e2f" } as const
const HARMONIC = { label: "Harmonic", icon: "signal", color: "#6f3ff5" } as const
const GOOGLE = { icon: ["fab", "google"], color: "#4285f4" } as const

const AIRTABLE_URL = "https://airtable.com/appJOfBzzLguLd91L/{id}"

/** Build a config, copying the shared brand's icon tuple so no two entries share
 *  one array. */
function source(
  config: Omit<SourceTypeConfig, "icon"> & { icon: string | readonly [string, string] }
): SourceTypeConfig {
  const { icon, ...rest } = config
  return { ...rest, icon: typeof icon === "string" ? icon : [icon[0], icon[1]] }
}

/**
 * Every source type, keyed by full type. Checked against what the sync code
 * writes; the Pipedrive activity, note and mail types are written by the SQL
 * importers in backend/db/sql/pipedrive_create_*.sql.
 */
export const SOURCE_REGISTRY: Readonly<Record<string, SourceTypeConfig>> = Object.fromEntries(
  [
    // People ------------------------------------------------------------------
    source({
      ...PIPEDRIVE,
      type: "pipedrive_person",
      key: "pipedrive",
      recordType: "person",
      externalUrlTemplate: "https://heavybit.pipedrive.com/person/{id}",
      isSearchable: true,
    }),
    source({
      ...PIPEDRIVE,
      type: "pipedrive_user_person",
      key: "pipedrive_user",
      recordType: "person",
      label: "Pipedrive User",
      icon: "user-lock",
      externalUrlTemplate: "https://heavybit.pipedrive.com/users/details/{id}",
      isSearchable: true,
    }),
    source({
      ...AIRTABLE,
      type: "airtable_person",
      key: "airtable",
      recordType: "person",
      externalUrlTemplate: AIRTABLE_URL,
      isSearchable: true,
    }),
    source({
      ...HUBSPOT,
      type: "hubspot_person",
      key: "hubspot",
      recordType: "person",
      externalUrlTemplate: "https://app.hubspot.com/contacts/44308902/record/0-1/{id}",
      isSearchable: true,
    }),
    source({
      ...LUMA,
      type: "luma_person",
      key: "luma",
      recordType: "person",
      externalUrlTemplate: "https://lu.ma/user/{id}",
      isSearchable: true,
    }),
    source({
      type: "mailchimp_person",
      key: "mailchimp",
      recordType: "person",
      label: "Mailchimp",
      icon: ["fab", "mailchimp"],
      color: "#ffb23e",
      externalUrlTemplate: "https://us20.admin.mailchimp.com/lists/members/view?id={id}",
      isSearchable: true,
    }),
    // Slack users have no stable external URL.
    source({ ...SLACK, type: "slack_person", key: "slack", recordType: "person", isSearchable: true }),
    source({
      ...SANITY,
      type: "sanity_person",
      key: "sanity",
      recordType: "person",
      externalUrlTemplate: "https://heavybit.sanity.studio/desk/person;{id}",
      isSearchable: true,
    }),
    source({
      ...HARMONIC,
      type: "harmonic_person",
      key: "harmonic",
      recordType: "person",
      externalUrlTemplate: "https://console.harmonic.ai/dashboard/person/{id}",
      isSearchable: true,
    }),

    // Organizations -----------------------------------------------------------
    source({
      ...PIPEDRIVE,
      type: "pipedrive_organization",
      key: "pipedrive",
      recordType: "organization",
      externalUrlTemplate: "https://heavybit.pipedrive.com/organization/{id}",
      isSearchable: true,
    }),
    source({
      ...AIRTABLE,
      type: "airtable_organization",
      key: "airtable",
      recordType: "organization",
      externalUrlTemplate: AIRTABLE_URL,
      isSearchable: true,
    }),
    source({
      ...HUBSPOT,
      type: "hubspot_organization",
      key: "hubspot",
      recordType: "organization",
      externalUrlTemplate: "https://app.hubspot.com/contacts/44308902/record/0-2/{id}",
      isSearchable: true,
    }),
    source({
      ...SLACK,
      type: "slack_organization",
      key: "slack",
      recordType: "organization",
      isSearchable: false,
    }),
    source({
      ...SANITY,
      type: "sanity_organization",
      key: "sanity",
      recordType: "organization",
      externalUrlTemplate: "https://studio.heavybit.com/content/community;allOrganizations;{id}",
      isSearchable: true,
    }),
    source({
      type: "standard_metrics_organization",
      key: "standard_metrics",
      recordType: "organization",
      label: "Standard Metrics",
      icon: "chart-line",
      color: "#6366f1",
      isSearchable: true,
    }),
    source({
      ...HARMONIC,
      type: "harmonic_organization",
      key: "harmonic",
      recordType: "organization",
      externalUrlTemplate: "https://console.harmonic.ai/dashboard/company/{id}",
      isSearchable: true,
    }),

    // Deals -------------------------------------------------------------------
    source({
      ...PIPEDRIVE,
      type: "pipedrive_deal",
      key: "pipedrive",
      recordType: "deal",
      externalUrlTemplate: "https://heavybit.pipedrive.com/deal/{id}",
      isSearchable: false,
    }),

    // Events ------------------------------------------------------------------
    // A Pipedrive activity imported as an event (pipedrive_create_activities.sql).
    source({
      ...PIPEDRIVE,
      type: "pipedrive_event",
      key: "pipedrive",
      recordType: "event",
      isSearchable: false,
    }),
    source({
      ...GOOGLE,
      type: "gcal_event",
      key: "gcal",
      recordType: "event",
      label: "Google Calendar",
      isSearchable: false,
    }),
    source({
      ...LUMA,
      type: "luma_event",
      key: "luma",
      recordType: "event",
      externalUrlTemplate: "https://lu.ma/{id}",
      isSearchable: true,
    }),
    source({
      ...AIRTABLE,
      type: "airtable_event",
      key: "airtable",
      recordType: "event",
      externalUrlTemplate: AIRTABLE_URL,
      isSearchable: true,
    }),
    source({
      ...AIRTABLE,
      type: "airtable_global_event",
      key: "airtable_global",
      recordType: "event",
      label: "Airtable Global Events",
      externalUrlTemplate: AIRTABLE_URL,
      isSearchable: true,
    }),
    source({
      ...SANITY,
      type: "sanity_event",
      key: "sanity",
      recordType: "event",
      externalUrlTemplate: "https://heavybit.sanity.studio/desk/event;{id}",
      isSearchable: true,
    }),
    source({
      ...SANITY,
      type: "sanity_devguild_event",
      key: "sanity_devguild",
      recordType: "event",
      label: "Sanity DevGuild",
      externalUrlTemplate: "https://heavybit.sanity.studio/desk/devGuildEvent;{id}",
      isSearchable: true,
    }),

    // Documents: saved emails and threads, and Pipedrive notes ----------------
    // Identity convention: `email_message` carries the RFC 5322 Message-ID —
    // the same value in every mailbox, so it's the canonical, provider-agnostic
    // identity and dedupe key for a saved email. The gmail_* types are
    // per-mailbox decorations (Gmail ids differ between users' mailboxes).
    source({
      type: "email_message",
      key: "email",
      recordType: "document",
      label: "Email",
      icon: "envelope",
      // rfc822msgid: search resolves the message in *any* user's own mailbox.
      externalUrlTemplate: "https://mail.google.com/mail/u/0/#search/rfc822msgid:{id}",
      isSearchable: false,
    }),
    source({
      ...GOOGLE,
      type: "gmail_message",
      key: "gmail",
      recordType: "document",
      label: "Gmail",
      // Gmail ids are mailbox-scoped: the link only resolves for the user whose
      // mailbox the message was saved from.
      externalUrlTemplate: "https://mail.google.com/mail/u/0/#all/{id}",
      isSearchable: false,
    }),
    source({
      ...GOOGLE,
      type: "gmail_thread",
      key: "gmail_thread",
      recordType: "document",
      label: "Gmail Thread",
      externalUrlTemplate: "https://mail.google.com/mail/u/0/#all/{id}",
      isSearchable: false,
    }),
    source({
      ...PIPEDRIVE,
      type: "pipedrive_mail_message",
      key: "pipedrive_mail",
      recordType: "document",
      label: "Pipedrive Email",
      isSearchable: false,
    }),
    source({
      ...PIPEDRIVE,
      type: "pipedrive_mail_thread",
      key: "pipedrive_mail_thread",
      recordType: "document",
      label: "Pipedrive Thread",
      isSearchable: false,
    }),
    // A Pipedrive note (pipedrive_create_notes.sql); the note editor syncs
    // edits back to it.
    source({
      ...PIPEDRIVE,
      type: "pipedrive_note",
      key: "pipedrive_note",
      recordType: "document",
      label: "Pipedrive Note",
      isSearchable: false,
    }),
    // An activity's note, split out into its own document
    // (pipedrive_create_activity_notes.sql).
    source({
      ...PIPEDRIVE,
      type: "pipedrive_activity_note",
      key: "pipedrive_activity_note",
      recordType: "document",
      label: "Pipedrive Activity Note",
      isSearchable: false,
    }),
  ].map((config) => [config.type, config])
)

/** A source type's config, or undefined for a type the registry doesn't hold. */
export function getSourceConfig(sourceType: string): SourceTypeConfig | undefined {
  return Object.hasOwn(SOURCE_REGISTRY, sourceType) ? SOURCE_REGISTRY[sourceType] : undefined
}

/** A source type's icon; a generic link icon for an unknown type. */
export function getSourceIcon(sourceType: string): SourceIcon {
  return getSourceConfig(sourceType)?.icon ?? "link"
}

/** A source type's label. An unknown type reads as its first segment,
 *  capitalized: "acme_person" → "Acme". */
export function getSourceLabel(sourceType: string): string {
  const config = getSourceConfig(sourceType)
  if (config) return config.label
  if (!sourceType) return "Unknown"
  return capitalize(sourceType.split("_")[0])
}

/** A source type's brand color, if it has one. */
export function getSourceColor(sourceType: string): string | undefined {
  return getSourceConfig(sourceType)?.color
}

/** A link to the source in its own system, or null when its type has none. */
export function getSourceExternalUrl(source: {
  source_type: string
  source_id: string
}): string | null {
  if (!source?.source_type || !source?.source_id) return null
  const template = getSourceConfig(source.source_type)?.externalUrlTemplate
  if (!template) return null
  // Encode the id: RFC 5322 Message-IDs contain <, > and @. Numeric and
  // alphanumeric ids used by other source types pass through unchanged.
  return template.replace("{id}", encodeURIComponent(source.source_id))
}

/**
 * A source type's short key: "pipedrive_person" → "pipedrive",
 * "pipedrive_user_person" → "pipedrive_user". An unknown type drops its last
 * segment (the record type, by convention).
 */
export function getSourceKey(sourceType: string): string {
  const config = getSourceConfig(sourceType)
  if (config) return config.key
  if (!sourceType) return ""
  const parts = sourceType.split("_")
  if (parts.length < 2) return sourceType
  return parts.slice(0, -1).join("_")
}

/**
 * The full source type for a short key on a record type:
 * ("person", "pipedrive_user") → "pipedrive_user_person".
 *
 * Null when the registry has no such pair — skip the source rather than
 * fabricate a malformed source_type.
 */
export function resolveSourceType(recordType: string, sourceKey: string): string | null {
  const match = Object.values(SOURCE_REGISTRY).find(
    (config) => config.key === sourceKey && config.recordType === recordType
  )
  return match?.type ?? null
}

/** Every source type of a record type. */
export function getSourceTypesForRecordType(recordType: string): string[] {
  return Object.values(SOURCE_REGISTRY)
    .filter((config) => config.recordType === recordType)
    .map((config) => config.type)
}

/** The source types of a record type the unlinked-source search covers. */
export function getSearchableSourcesForRecordType(recordType: string): SourceTypeConfig[] {
  return Object.values(SOURCE_REGISTRY).filter(
    (config) => config.recordType === recordType && config.isSearchable
  )
}

/** The short keys of a record type's searchable sources. */
export function getSearchableSourceKeysForRecordType(recordType: string): string[] {
  return getSearchableSourcesForRecordType(recordType).map((config) => config.key)
}
